import { ReactElement, useCallback, useState } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import ManuallyAddPlayerOption from "@/components/molecules/ManuallyAddPlayerOption";
import ManuallyAddPlayerModal from "@/components/organisms/ManuallyAddPlayerModal";
import SkeletonList from "@/components/organisms/SkeletonList";
import FilterResultsView from "./FilterResultsView";

import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import SearchResultsItem from "@/components/organisms/SearchResultsItem";
import { spacingForDevicesWithoutNotch } from "@/config/constants";
import { POSTHOG_FLAGS } from "@/config/featureFlags";
import { PlayerSearchPlayerFragment } from "@/generated/graphql";
import { useFilter } from "@/hooks/useFilter";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { useTheme } from "@/providers/ThemeProvider";

/** Threshold (0–1) for triggering load-more when scrolling near the bottom */
const END_REACHED_THRESHOLD = 0.8;

/**
 * Container component for displaying filtered player search results
 * @remarks
 * Manages filter result state, loading states, pagination, and the
 * "Manually Add Player" entry point gated behind a feature flag.
 */
function FilterResultsContainer() {
  const { bottom } = useSafeAreaInsets();
  const { loading, isRefreshing, players, handleLoadMore, handleRefresh } =
    useFilter();
  const isManualAddPlayerEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.MANUALLY_ADD_PLAYER) === true;
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const [isManualAddOpen, setIsManualAddOpen] = useState(false);

  const handleManualAddPlayer = useCallback(() => {
    setIsManualAddOpen(true);
  }, []);

  const handleManualAddClose = useCallback(() => {
    setIsManualAddOpen(false);
  }, []);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { layoutMeasurement, contentOffset, contentSize } =
        event.nativeEvent;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (
        distanceFromBottom <
        layoutMeasurement.height * END_REACHED_THRESHOLD
      ) {
        handleLoadMore();
      }
    },
    [handleLoadMore]
  );

  const renderItem = useCallback(
    ({ item }: { item: PlayerSearchPlayerFragment }): ReactElement => (
      <SearchResultsItem item={item} />
    ),
    []
  );

  const renderListFooterComponent = useCallback(
    () => (
      <>
        {loading && !isRefreshing && players.length ? (
          <Spinner size="large" color="white" />
        ) : null}
        {!loading && isManualAddPlayerEnabled && players.length > 0 ? (
          <ManuallyAddPlayerOption
            subtitle="Can't find who you're looking for?"
            onPress={handleManualAddPlayer}
            isDark={isDark}
            variant="search"
          />
        ) : null}
      </>
    ),
    [
      loading,
      isRefreshing,
      players.length,
      isManualAddPlayerEnabled,
      handleManualAddPlayer,
      isDark,
    ]
  );

  const renderListEmptyComponent = useCallback(
    () =>
      loading ? (
        <SkeletonList type="player" />
      ) : (
        <Stack className="h-4/5 items-center justify-center gap-3">
          <Text variant="display-sm" className="text-center">
            No Results
          </Text>
          <Text
            variant="title-sm-medium"
            className="text-center text-content-muted"
          >
            Go back and expand your filters to find players
          </Text>
          {isManualAddPlayerEnabled ? (
            <ManuallyAddPlayerOption
              subtitle="Can't find who you're looking for?"
              onPress={handleManualAddPlayer}
              isDark={isDark}
            />
          ) : null}
        </Stack>
      ),
    [loading, isManualAddPlayerEnabled, handleManualAddPlayer, isDark]
  );

  return (
    <>
      <FilterResultsView
        players={players}
        refreshing={isRefreshing && !!players.length}
        isShowPopoverFilter={loading || !!players.length}
        bottomInset={bottom + spacingForDevicesWithoutNotch}
        renderItem={renderItem}
        onScroll={handleScroll}
        onRefresh={handleRefresh}
        renderListFooterComponent={renderListFooterComponent}
        renderListEmptyComponent={renderListEmptyComponent}
      />
      {isManualAddPlayerEnabled ? (
        <ManuallyAddPlayerModal
          isOpen={isManualAddOpen}
          onClose={handleManualAddClose}
        />
      ) : null}
    </>
  );
}

export default FilterResultsContainer;
